'use client';

import { useCallback } from 'react';
import { useTranslations } from 'next-intl';

export type ProgramDisplay = {
  /** The localized parent-facing name, or the API-provided name as a fallback. */
  name: string;
  /** The localized one-line pitch, or `null` when this slug has no entry. */
  tagline: string | null;
};

/**
 * Parent-facing program names and taglines, keyed by SLUG (D6).
 *
 * `programs.name` is the ADMIN's label ("US Online", "Overseas") and is not
 * translated. What a visitor sees at signup is a separate, localized string, so
 * it lives in the message files instead — and an unrecognised slug falls back to
 * the API-provided name with no tagline rather than rendering a raw key or a
 * blank row.
 *
 * ⚠️ This hook binds its OWN translator on purpose. Its consumers hold `t` bound
 * to `auth` and to `parent.profile`; passing either one in would resolve
 * `auth.public.programs.display.…` and silently miss every lookup, falling back
 * to the untranslated admin name on every program. The whole catalog is shipped
 * to the client by `public-i18n-provider.tsx` and `admin-i18n-provider.tsx`
 * (which also wraps the parent shell), so this namespace resolves with no
 * provider change.
 */
export function useProgramDisplay() {
  const t = useTranslations('public.programs.display');

  return useCallback(
    (slug: string, fallbackName: string): ProgramDisplay => ({
      name: t.has(`${slug}.name`) ? t(`${slug}.name`) : fallbackName,
      tagline: t.has(`${slug}.tagline`) ? t(`${slug}.tagline`) : null,
    }),
    [t]
  );
}
